/**
 * SEO Landing Page - Stillas Privat Oslo
 * Public page for attracting private homeowners - houses, facades, renovation
 */
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import {
  Home,
  Building2,
  MapPin,
  CheckCircle2,
  Wrench,
  Shield,
  Phone,
  Mail,
  ChevronRight,
  Star,
  FileText,
  ArrowRight,
  PaintBucket,
  Hammer,
  Send,
  Heart,
  Clock,
  MessageSquare,
  ImageIcon,
  Play
} from "lucide-react";

export default function StillasPrivatOslo() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    description: '',
    phone: '',
    email: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const remainingSlots = 5 - images.length;
    if (files.length > remainingSlots) {
      toast.error(`Du kan bare legge til ${remainingSlots} bilde(r) til (maks 5)`);
      return;
    }

    setUploading(true);
    const uploadedUrls = [];
    
    // Upload one by one (more reliable than parallel)
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} er for stor (maks 5MB)`);
        continue;
      }
      
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('folder', 'public_quotes');
        
        const res = await fetch(`${API_URL}/api/projects-v3/public-upload`, {
          method: 'POST',
          body: formDataUpload
        });
        
        if (res.ok) {
          const data = await res.json();
          uploadedUrls.push(data.secure_url);
          toast.success(`Bilde ${i + 1}/${files.length} lastet opp`);
        } else {
          toast.error(`Kunne ikke laste opp ${file.name}`);
        }
      } catch (error) {
        toast.error(`Feil ved opplasting av ${file.name}`);
      }
    }
    
    if (uploadedUrls.length > 0) {
      setImages(prev => [...prev, ...uploadedUrls]);
    }
    setUploading(false);
    
    // Reset input so same files can be selected again
    e.target.value = '';
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const res = await fetch(`${API_URL}/api/projects-v3/stillastjenester/public-quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contact_person: formData.name,
          project_address: formData.address,
          description: formData.description,
          phone: formData.phone,
          email: formData.email,
          company_name: '',
          source_page: 'private',
          images: images
        })
      });

      if (!res.ok) throw new Error('Failed to submit');
      
      toast.success('Takk! Vi tar kontakt snart.');
      setFormData({
        name: '',
        address: '',
        description: '',
        phone: '',
        email: ''
      });
      setImages([]);
    } catch (error) {
      toast.error('Kunne ikke sende forespørsel. Prøv igjen.');
    } finally {
      setSubmitting(false);
    }
  };

  // FAQ data (Norwegian only)
  const faqData = [
    { q: "Trenger jeg å gjøre noe før montering?", a: "Nei, vi tar oss av planlegging og montering." },
    { q: "Hvor lenge kan stillaset stå?", a: "Stillaset kan stå så lenge prosjektet krever – dette avtales på forhånd." },
    { q: "Er stillas trygt for privat bruk?", a: "Ja, vi bruker profesjonelle systemer og monterer etter gjeldende krav." },
    { q: "Hvor raskt kan dere levere?", a: "Ofte innen kort tid, avhengig av kapasitet og prosjektets omfang." }
  ];

  // FAQ Schema for rich results (always in Norwegian for Google.no)
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Trenger jeg å gjøre noe før montering av stillas?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Nei, vi tar oss av planlegging og montering. Du trenger ikke å forberede noe spesielt."
        }
      },
      {
        "@type": "Question",
        "name": "Hvor lenge kan stillaset stå oppe?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Stillaset kan stå så lenge prosjektet krever – dette avtales på forhånd basert på dine behov."
        }
      },
      {
        "@type": "Question",
        "name": "Er stillas trygt for privat bruk?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Ja, vi bruker profesjonelle LAYHER og HAKI systemer og monterer etter gjeldende HMS-krav for maksimal sikkerhet."
        }
      },
      {
        "@type": "Question",
        "name": "Hvor raskt kan dere levere stillas til min bolig?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Ofte innen kort tid, avhengig av kapasitet og prosjektets omfang. Ta kontakt for en konkret vurdering."
        }
      }
    ]
  };

  // LocalBusiness Schema
  const businessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "AllStillas",
    "description": "Stillas til privat bolig i Oslo. Sikre og fleksible stillasløsninger for hus, fasade og rehabilitering.",
    "url": "https://allstillas.no",
    "telephone": "+47 93 92 28 19",
    "email": "support@allstillas.no",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Oslo",
      "addressRegion": "Oslo",
      "addressCountry": "NO"
    },
    "areaServed": ["Oslo", "Viken"],
    "serviceType": ["Scaffolding for Private Homes", "Residential Scaffolding", "Facade Scaffolding"]
  };
  
  return (
    <>
      {/* SEO Meta Tags - Keep in Norwegian for Google.no */}
      <Helmet>
        <title>Stillas til privat bolig i Oslo | Trygge løsninger – AllStillas</title>
        <meta 
          name="description" 
          content="Trenger du stillas til privat bolig i Oslo? AllStillas leverer sikre og fleksible stillasløsninger for hus, fasade og rehabilitering. Få tilbud."
        />
        <meta name="keywords" content="stillas privat Oslo, stillas bolig, stillas hus, stillas fasade, stillas rehabilitering, stillas privatkunde Oslo" />
        <link rel="canonical" href="https://allstillas.no/stillas-privat-oslo" />
        
        {/* hreflang for language versions - NO is primary */}
        <link rel="alternate" hreflang="no" href="https://allstillas.no/stillas-privat-oslo" />
        <link rel="alternate" hreflang="en" href="https://allstillas.no/stillas-privat-oslo?lang=en" />
        <link rel="alternate" hreflang="x-default" href="https://allstillas.no/stillas-privat-oslo" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Stillas til privat bolig i Oslo | Trygge løsninger – AllStillas" />
        <meta property="og:description" content="Trenger du stillas til privat bolig i Oslo? Sikre og fleksible stillasløsninger for hus, fasade og rehabilitering." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://allstillas.no/stillas-privat-oslo" />
        <meta property="og:locale" content="nb_NO" />
        
        {/* LocalBusiness Schema */}
        <script type="application/ld+json">
          {JSON.stringify(businessSchema)}
        </script>
        
        {/* FAQ Schema for Rich Results */}
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#050505]">
        {/* Navigation Bar */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-md border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-1">
                <div className="px-2 py-1 bg-[#0A0A0A] border border-[#23F7DD]/50 rounded flex items-center justify-center">
                  <span className="text-[#23F7DD] font-bold text-base font-['Unbounded']">All</span>
                </div>
                <span className="text-lg font-bold font-['Unbounded'] text-white">Stillas</span>
              </Link>
              
              <div className="flex items-center gap-2 sm:gap-3">
                <Link to="/">
                  <Button variant="ghost" className="text-white hover:text-[#23F7DD] hidden sm:inline-flex">
                    {t("platformGuide.home")}
                  </Button>
                </Link>
                <Link to="/hvordan-det-fungerer">
                  <Button variant="ghost" className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/10 gap-2 font-semibold hidden sm:inline-flex">
                    <Play className="w-4 h-4" />{t("platformGuide.home") === "Hjem" ? "Hvordan det fungerer" : "How it works"}
                  </Button>
                </Link>
                <Link to="/hvordan-det-fungerer">
                  <Button size="sm" className="bg-orange-500 text-white hover:bg-orange-400 font-bold px-3 text-xs sm:hidden">
                    Info
                  </Button>
                </Link>
                <LanguageSwitcher variant="full" className="text-white" />
                <Link to="/login">
                  <Button variant="ghost" className="text-white hover:text-[#23F7DD] hidden sm:inline-flex">
                    {t("stillasfirma.login")}
                  </Button>
                </Link>
                <a href="#tilbud">
                  <Button className="bg-[#F7931A] text-white hover:bg-[#FF9F2E] font-bold text-sm">
                    {t("stillasPrivat.ctaQuote")}
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 px-4 overflow-hidden">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#F7931A]/5 via-transparent to-transparent" />
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#F7931A]/10 rounded-full blur-3xl" />
          <div className="absolute top-40 right-1/4 w-64 h-64 bg-[#23F7DD]/10 rounded-full blur-3xl" />
          
          {/* Easter Promotion Banner */}
          <div className="max-w-3xl mx-auto mb-8 relative z-20">
            <div className="bg-gradient-to-r from-[#F7931A] via-[#FFB347] to-[#F7931A] py-4 px-6 rounded-2xl shadow-lg shadow-[#F7931A]/30 border border-[#FFD700]/30">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">🐣</span>
                  <span className="text-[#050505] font-bold text-sm sm:text-base">
                    {t("stillasPrivat.easterPromoTitle")}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-center">
                  <span className="bg-[#050505] text-[#F7931A] font-black text-lg sm:text-xl px-4 py-1 rounded-full">
                    5-15% {t("stillasPrivat.easterDiscount")}
                  </span>
                </div>
                <span className="text-2xl">🐰</span>
              </div>
              <p className="text-[#050505]/80 text-xs sm:text-sm text-center mt-2">
                {t("stillasPrivat.easterPromoSubtitle")} <span className="font-bold text-[#050505]">10. april 2025</span>
              </p>
            </div>
          </div>
          
          <div className="max-w-4xl mx-auto text-center relative z-10">
            {/* Badge with Cross-Link */}
            <div className="flex items-center justify-center gap-3 mb-6 flex-wrap">
              {/* Current Page Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#F7931A]/10 border border-[#F7931A]/30 rounded-full">
                <Home className="w-4 h-4 text-[#F7931A]" />
                <span className="text-sm text-[#F7931A] font-medium">{t("stillasPrivat.badge")}</span>
              </div>
              
              {/* Cross-link to B2B Page */}
              <Link to="/stillasfirma-oslo">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#4F46E5]/10 border border-[#4F46E5]/30 rounded-full hover:bg-[#4F46E5]/20 transition-colors cursor-pointer">
                  <Building2 className="w-4 h-4 text-[#4F46E5]" />
                  <span className="text-sm text-[#4F46E5] font-medium">{t("stillasPrivat.forBusinessLink")}</span>
                  <ChevronRight className="w-4 h-4 text-[#4F46E5]" />
                </div>
              </Link>
            </div>
            
            {/* H1 - Main SEO Title */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Unbounded'] font-bold text-white mb-6 leading-tight">
              {t("stillasPrivat.heroTitle")}
              <span className="block text-[#F7931A] mt-2">{t("stillasPrivat.heroSubtitle")}</span>
            </h1>
            
            {/* Intro Text */}
            <p className="text-lg sm:text-xl text-[#94A3B8] mb-4 max-w-2xl mx-auto leading-relaxed">
              {t("stillasPrivat.heroText")}
            </p>
            
            <p className="text-[#94A3B8] text-base mb-8 max-w-2xl mx-auto">
              {t("stillasPrivat.heroText2")}
            </p>
            
            {/* Primary CTA */}
            <a href="#tilbud">
              <Button 
                size="lg" 
                className="bg-[#F7931A] text-white hover:bg-[#FF9F2E] font-bold text-lg px-8 py-6 rounded-xl shadow-lg shadow-[#F7931A]/20 group"
                data-testid="hero-cta-quote"
              >
                <FileText className="w-5 h-5 mr-2" />
                {t("stillasPrivat.ctaButton")}
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
          </div>
        </section>

        {/* Who Is This For Section */}
        <section className="py-20 px-4 bg-[#0A0A0A]">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-['Unbounded'] font-bold text-white mb-4">
                👨‍👩‍👧 {t("stillasPrivat.whoIsThisFor")}
              </h2>
              <p className="text-[#94A3B8] max-w-2xl mx-auto">
                {t("stillasPrivat.whoIsThisForSubtitle")}
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: <Home className="w-6 h-6" />, text: t("stillasPrivat.whoItem1") },
                { icon: <PaintBucket className="w-6 h-6" />, text: t("stillasPrivat.whoItem2") },
                { icon: <Hammer className="w-6 h-6" />, text: t("stillasPrivat.whoItem3") },
                { icon: <Heart className="w-6 h-6" />, text: t("stillasPrivat.whoItem4") },
              ].map((item, i) => (
                <Card key={i} className="bg-white/5 border-white/10 hover:border-[#F7931A]/30 transition-colors">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 bg-[#F7931A]/10 rounded-xl flex items-center justify-center mx-auto mb-4 text-[#F7931A]">
                      {item.icon}
                    </div>
                    <p className="text-white font-medium">{item.text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            <p className="text-center text-[#23F7DD] mt-8 text-lg">
              👉 {t("stillasPrivat.smallProjectsNote")}
            </p>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div>
                <h2 className="text-3xl sm:text-4xl font-['Unbounded'] font-bold text-white mb-6">
                  🔧 {t("stillasPrivat.whatWeOffer")}
                </h2>
                <ul className="space-y-4">
                  {[
                    t("stillasPrivat.offerItem1"),
                    t("stillasPrivat.offerItem2"),
                    t("stillasPrivat.offerItem3"),
                    t("stillasPrivat.offerItem4"),
                    t("stillasPrivat.offerItem5")
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-lg text-[#94A3B8]">
                      <CheckCircle2 className="w-5 h-5 text-[#F7931A] flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-[#64748B] text-sm mt-6 italic">
                  {t("stillasPrivat.offerNote")}
                </p>
              </div>
              
              <div>
                <h2 className="text-3xl sm:text-4xl font-['Unbounded'] font-bold text-white mb-6">
                  🛡️ {t("stillasPrivat.safetyTitle")}
                </h2>
                <p className="text-[#94A3B8] mb-6">{t("stillasPrivat.safetySubtitle")}</p>
                <ul className="space-y-4">
                  {[
                    t("stillasPrivat.safetyItem1"),
                    t("stillasPrivat.safetyItem2"),
                    t("stillasPrivat.safetyItem3"),
                    t("stillasPrivat.safetyItem4")
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-lg text-[#94A3B8]">
                      <Shield className="w-5 h-5 text-[#23F7DD] flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-[#F7931A] mt-6 font-medium">
                  {t("stillasPrivat.safetyNote")}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Work Area Section */}
        <section className="py-20 px-4 bg-[#0A0A0A]">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-['Unbounded'] font-bold text-white mb-6">
              📍 {t("stillasPrivat.workAreaTitle")}
            </h2>
            <p className="text-[#94A3B8] text-lg mb-8">
              {t("stillasPrivat.workAreaSubtitle")}
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {[t("stillasPrivat.workAreaOslo"), t("stillasPrivat.workAreaViken"), t("stillasPrivat.workAreaNearby")].map((area, i) => (
                <div 
                  key={i}
                  className="px-6 py-3 bg-white/5 border border-white/10 rounded-full text-white font-medium flex items-center gap-2"
                >
                  <MapPin className="w-4 h-4 text-[#F7931A]" />
                  {area}
                </div>
              ))}
            </div>
            
            {/* SEO Local Paragraph */}
            <p className="text-[#64748B] text-sm max-w-2xl mx-auto leading-relaxed">
              {t("stillasPrivat.workAreaSeo")}
            </p>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-['Unbounded'] font-bold text-white mb-12 text-center">
              ⭐ {t("stillasPrivat.whyChooseTitle")}
            </h2>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: <CheckCircle2 className="w-6 h-6" />, text: t("stillasPrivat.whyItem1") },
                { icon: <Clock className="w-6 h-6" />, text: t("stillasPrivat.whyItem2") },
                { icon: <Wrench className="w-6 h-6" />, text: t("stillasPrivat.whyItem3") },
                { icon: <Star className="w-6 h-6" />, text: t("stillasPrivat.whyItem4") },
                { icon: <MessageSquare className="w-6 h-6" />, text: t("stillasPrivat.whyItem5") },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-white/5 rounded-xl border border-white/10">
                  <div className="text-[#F7931A]">{item.icon}</div>
                  <span className="text-white">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Quote Form Section */}
        <section id="tilbud" className="py-20 px-4 bg-[#0A0A0A]">
          <div className="max-w-xl mx-auto">
            <Card className="bg-gradient-to-br from-[#F7931A]/10 to-[#23F7DD]/5 border-[#F7931A]/30">
              <CardContent className="p-8 sm:p-10">
                <div className="text-center mb-8">
                  <Home className="w-14 h-14 text-[#F7931A] mx-auto mb-4" />
                  <h2 className="text-2xl sm:text-3xl font-['Unbounded'] font-bold text-white mb-2">
                    {t("stillasPrivat.formTitle")}
                  </h2>
                  <p className="text-[#94A3B8]">{t("stillasPrivat.formSubtitle")}</p>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label className="text-white mb-2 block">{t("stillasPrivat.yourName")}</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder={t("stillasPrivat.namePlaceholder")}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label className="text-white mb-2 block">{t("stillasPrivat.address")}</Label>
                    <Input
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      placeholder={t("stillasPrivat.addressPlaceholder")}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label className="text-white mb-2 block">{t("stillasPrivat.description")}</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder={t("stillasPrivat.descriptionPlaceholder")}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40 min-h-[80px]"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white mb-2 block">{t("stillasPrivat.phone")}</Label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        placeholder={t("stillasPrivat.phonePlaceholder")}
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-white mb-2 block">{t("stillasPrivat.email")}</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder={t("stillasPrivat.emailPlaceholder")}
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                        required
                      />
                    </div>
                  </div>

                  {/* Image Upload Section */}
                  <div>
                    <Label className="text-white mb-2 block">{t("stillasPrivat.addImages")}</Label>
                    <div className="space-y-3">
                      <label className="flex items-center justify-center gap-2 px-4 py-3 bg-white/5 border border-dashed border-white/20 rounded-lg cursor-pointer hover:bg-white/10 transition-colors">
                        <ImageIcon className="w-5 h-5 text-[#F7931A]" />
                        <span className="text-white/70">{uploading ? t("stillasPrivat.sending") : t("stillasPrivat.maxImages")}</span>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageUpload}
                          className="hidden"
                          disabled={uploading || images.length >= 5}
                        />
                      </label>
                      
                      {images.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {images.map((url, index) => (
                            <div key={index} className="relative group">
                              <img 
                                src={url} 
                                alt={`Upload ${index + 1}`} 
                                className="w-20 h-20 object-cover rounded-lg border border-white/20"
                              />
                              <button
                                type="button"
                                onClick={() => removeImage(index)}
                                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <Button 
                    type="submit"
                    size="lg"
                    className="w-full bg-[#F7931A] text-white hover:bg-[#FF9F2E] font-bold mt-2"
                    disabled={submitting || uploading}
                  >
                    {submitting ? (
                      <>{t("stillasPrivat.sending")}</>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        {t("stillasPrivat.sendRequest")}
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-['Unbounded'] font-bold text-white mb-12 text-center">
              ❓ {t("stillasPrivat.faqTitle")}
            </h2>
            
            <div className="space-y-6">
              {faqData.map((faq, i) => (
                <Card key={i} className="bg-white/5 border-white/10">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-white mb-2 flex items-start gap-2">
                      <span className="text-[#F7931A]">Q:</span>
                      {faq.q}
                    </h3>
                    <p className="text-[#94A3B8] pl-6">
                      <span className="text-[#23F7DD] font-bold">A:</span> {faq.a}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20 px-4 bg-[#0A0A0A]">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-['Unbounded'] font-bold text-white mb-6">
              📞 {t("stillasPrivat.contactTitle")}
            </h2>
            <p className="text-[#94A3B8] text-lg mb-8">
              {t("stillasPrivat.contactText")}
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <a 
                href="mailto:support@allstillas.no"
                className="flex items-center gap-3 px-6 py-4 bg-white/5 border border-white/10 rounded-xl hover:border-[#F7931A]/30 transition-colors"
              >
                <Mail className="w-5 h-5 text-[#F7931A]" />
                <span className="text-white">support@allstillas.no</span>
              </a>
              <a 
                href="tel:+4793922819"
                className="flex items-center gap-3 px-6 py-4 bg-white/5 border border-white/10 rounded-xl hover:border-[#F7931A]/30 transition-colors"
              >
                <Phone className="w-5 h-5 text-[#F7931A]" />
                <span className="text-white">+47 93 92 28 19</span>
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 px-4 border-t border-white/10">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-2">
                <Link to="/" className="flex items-center gap-1">
                  <div className="px-2 py-1 bg-[#0A0A0A] border border-[#23F7DD]/50 rounded">
                    <span className="text-[#23F7DD] font-bold text-sm font-['Unbounded']">All</span>
                  </div>
                  <span className="text-sm font-bold font-['Unbounded'] text-white">Stillas</span>
                </Link>
                <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#23F7DD]/10 border border-[#23F7DD]/20 rounded-full">
                  <span className="text-[#23F7DD] text-[10px]">⚡</span>
                  <span className="text-[10px] text-[#94A3B8]">Powered by</span>
                  <span className="text-[10px] text-[#23F7DD]">Doserne AS</span>
                </div>
              </div>
              
              <p className="text-sm text-[#64748B]">© 2026 Doserne AS. All rights reserved.</p>
              
              <div className="flex items-center gap-4 text-sm text-[#64748B]">
                <Link to="/stillasfirma-oslo" className="hover:text-white transition-colors">{t("stillasPrivat.forBusiness")}</Link>
                <Link to="/stillasjobb-oslo" className="hover:text-white transition-colors">{t("stillasPrivat.career")}</Link>
                <Link to="/privacy" className="hover:text-white transition-colors">{t("stillasPrivat.privacy")}</Link>
              </div>
            </div>
            
            {/* SEO Boost Text */}
            <p className="text-xs text-[#475569] text-center">
              {t("stillasPrivat.seoText")}
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}
